/**
 * Display formatting helpers — money, references, names.
 * All money values internally are integer pence.
 */

/** Format an integer pence value as GBP, e.g. 22050 -> "£220.50". */
export function formatPence(pence: number): string {
  const sign = pence < 0 ? '-' : '';
  const abs = Math.abs(Math.round(pence));
  const pounds = Math.floor(abs / 100);
  const remainder = abs % 100;
  const poundsFormatted = pounds.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}£${poundsFormatted}.${remainder.toString().padStart(2, '0')}`;
}

/** Pluralise a unit. e.g. pluralise(1, "night") -> "1 night". */
export function pluralise(n: number, unit: string, pluralForm?: string): string {
  if (n === 1) return `1 ${unit}`;
  return `${n} ${pluralForm ?? unit + 's'}`;
}

/** Trim and collapse whitespace in user-entered text. */
export function cleanText(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/** Minimal RFC-5322-flavoured email validation, good enough for client-side. */
export function isValidEmail(value: string): boolean {
  const v = value.trim();
  if (v.length === 0 || v.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/**
 * Lightweight UK / international phone validation.
 * Accepts digits, spaces, +, -, () . Must contain at least 7 digits.
 */
export function isValidPhone(value: string): boolean {
  const v = value.trim();
  const digits = v.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return false;
  return /^[+]?[\d\s\-().]+$/.test(v);
}

/** Escape HTML for safe interpolation. */
export function escapeHTML(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/`/g, '&#96;')
    .replace(/\//g, '&#x2F;');
}
